import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useDatabaseContext } from './DatabaseContext';

type WeightUnit = 'LBS' | 'KGS';

interface PreferencesContextType {
  weightUnit: WeightUnit;
  setWeightUnit: (unit: WeightUnit) => void;
  convertWeight: (value: number, fromUnit?: WeightUnit, toUnit?: WeightUnit) => number;
  formatWeight: (value: number, includeUnit?: boolean) => string;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

interface PreferencesProviderProps {
  children: ReactNode;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({ children }) => {
  const { apiUrl } = useDatabaseContext();
  const [weightUnit, setWeightUnitState] = useState<WeightUnit>('LBS');

  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await fetch(`${apiUrl}/system-preferences`);
        if (response.ok) {
          const data = await response.json();
          if (data.weightUnit) {
            setWeightUnitState(data.weightUnit);
          }
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    };
    loadPreferences();
  }, [apiUrl]);

  const setWeightUnit = async (unit: WeightUnit) => {
    setWeightUnitState(unit);
    
    // Save to database
    try {
      const response = await fetch(`${apiUrl}/system-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weightUnit: unit })
      });
      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to update weight unit:', error);
      }
    } catch (error) {
      console.error('Error updating weight unit:', error);
    }
  };

  // Convert weight between units
  const convertWeight = (value: number, fromUnit: WeightUnit = 'LBS', toUnit: WeightUnit = weightUnit): number => {
    if (fromUnit === toUnit) return value;
    
    // LBS to KGS: multiply by 0.453592
    // KGS to LBS: multiply by 2.20462
    if (fromUnit === 'LBS' && toUnit === 'KGS') {
      return value * 0.453592;
    } else if (fromUnit === 'KGS' && toUnit === 'LBS') {
      return value * 2.20462;
    }
    
    return value;
  };

  // Format weight with optional unit display
  const formatWeight = (value: number, includeUnit: boolean = true): string => {
    const formatted = value.toFixed(2);
    return includeUnit ? `${formatted} ${weightUnit}` : formatted;
  };

  return (
    <PreferencesContext.Provider value={{
      weightUnit,
      setWeightUnit,
      convertWeight,
      formatWeight
    }}>
      {children}
    </PreferencesContext.Provider>
  );
};